// @flow
import React, {Component} from 'react';
import {isEqual, pick} from 'lodash';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {graphql} from 'react-apollo';
import searchQuery, {
    FORCE_GRAPHQL_ERROR_HEADER,
    searchQueryParams
} from '../../queries/searchQuery';
import {ResultsParams} from '../../../server/graphql/params';
import searchQueryActions from '../../actions/searchQuery';
import isBoundingBoxSearch from '../../utils/isBoundingBoxSearch';

import type {ComponentType} from 'react';
import type {SearchQueryResponseData} from '../../../../src/universal/queries/searchQuery';
import type {State as ReduxState} from '../../reducers/index';
import type {SearchParamsType} from '../../reducers/searchParams';

type DispatchProps = {
    searchQueryComplete: typeof searchQueryActions.searchQueryComplete
};

type StateProps = {
    siteName: string
} & SearchParamsType;

type ConnectorProps = DispatchProps &
    StateProps & {
        data: SearchQueryResponseData
    };

export default function withSearchQuery<OwnProps: {}>(
    WrappedComponent: ComponentType<$Supertype<OwnProps>>
): ComponentType<OwnProps> {
    type Props = OwnProps & ConnectorProps;

    class WithSearchQuery extends Component<Props> {
        componentDidMount() {
            const {data, searchQueryComplete} = this.props;
            if (!data.loading) {
                searchQueryComplete(data);
            }
        }

        shouldComponentUpdate(nextProps: Props) {
            const {loading, results, error} = this.props.data;
            const nextLoading = nextProps.data.loading;
            const nextResults = nextProps.data.results;
            const nextError = nextProps.data.error;

            return (
                loading !== nextLoading ||
                !isEqual(results, nextResults) ||
                !isEqual(error, nextError)
            );
        }

        componentDidUpdate() {
            const {data, searchQueryComplete} = this.props;
            // we need only check loading since shouldComponentUpdate
            // guarantees that app has either moved to a loading state
            // or results have changed
            if (!data.loading) {
                searchQueryComplete(data);
            }
        }

        render() {
            return <WrappedComponent {...this.props} />;
        }
    }

    const mapStateToGraphlProps = ({
        searchParams,
        searchTerm,
        system,
        router: {pathname}
    }: ReduxState): StateProps => {
        let result = {
            siteName: system.siteName,
            pathname,
            ...pick(searchParams.params, ResultsParams)
        };
        if (
            isBoundingBoxSearch(searchParams.params) &&
            !searchParams.params.searchTermUuid &&
            searchTerm.id
        ) {
            result.searchTermUuid = searchTerm.id;
        }
        return result;
    };

    const mapDispatchToProps = {
        searchQueryComplete: searchQueryActions.searchQueryComplete
    };

    return compose(
        connect(
            mapStateToGraphlProps,
            mapDispatchToProps
        ),
        graphql(searchQuery, {
            options: (props) => ({
                errorPolicy: 'all',
                context: {
                    headers: {
                        [FORCE_GRAPHQL_ERROR_HEADER]:
                            typeof window !== 'undefined' &&
                            window.forceGraphQLError
                    }
                },
                variables: {
                    params: searchQueryParams(props.siteName, props),
                    url: props.pathname
                },
                ssr: false // don't need to ssr here, since we already fetched in the application route handler
            })
        })
    )(WithSearchQuery);
}
